import { Box, Divider } from '@eatclubapp/ec-component-library';
import { useTheme } from '@material-ui/core';
import { useRollbar } from '@rollbar/react';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from '../../../components/Button';
import { Spacer } from '../../../components/Spacer';
import { Typography } from '../../../components/Typography';
import { updateBookingAction } from '../../../data/actions/bookingAction';
import { fetchBookingTermsAction } from '../../../data/actions/bookingTermsAction';
import {
  holdSelectedRoomsAction,
  updateHeldExperienceAction,
} from '../../../data/actions/experienceAction';
import { holdBookingAction } from '../../../data/actions/obeeBookingAction';
import { bookingKeys, bookingPropTypes } from '../../../data/models/Booking';
import { accommodationPropTypes, addonGroupPropTypes } from '../../../data/models/Experience';
import { restaurantPropTypes } from '../../../data/models/Restaurant';
import { obeeBookingPropTypes } from '../../../data/models/obeeBooking';
import { trackEvent } from '../../../utils/analytics';
import { ERROR_MESSAGES } from '../../../utils/constants';
import { devLog } from '../../../utils/devLog';
import { findObjectByProperty, isEmpty } from '../../../utils/helpers';
import { useIsMobile } from '../../../utils/hooks';
import { safeParse } from '../../../utils/stringHelpers';
import AddonOption from './AddonOption';
import AddonOptionNoImage from './AddonOptionNoImage';
import useStyles from './AddonsStyles';

const AddonGroup = ({
  addonGroup,
  booking,
  updateBooking,
  onNext,
  groupIndex,
  updateHeldExperience,
  holdSelectedRooms,
  accommodation,
  isAccommodation,
  obeeBooking,
  restaurant,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const rollbar = useRollbar();
  const isMobile = useIsMobile();

  const [addonCounts, setAddonCounts] = useState(booking?.addons || {});

  const [_countErrors, setCountErrors] = useState(null);

  // // If addon group is an "accommodation", then we have a different flow and we choose the room instead?
  // useEffect(() => {
  //   if (addonGroup?.experienceTypeSettings?.accommodation) {
  //     getExperienceAccommodation(addonGroup?.experienceId, booking?.date);
  //   }
  // }, []);

  // Get list of addons to show
  const filteredAddons = addonGroup?.addons?.filter((addon) => addon?.availableQuantity > 0) ?? [];
  const hasImages = filteredAddons?.some((addon) => !isEmpty(addon?.imageUrl));

  // const filteredAddons = useMemo(() => {
  //   return addonGroup?.addons.filter((addon) => {
  //     return (
  //       (!addon.min || addon.min <= booking.guests) && (!addon.max || addon.max >= booking.guests)
  //     );
  //
  //     // TODO check day is in valid days of week
  //     // TODO check date is in date range
  //   });
  // }, [addonGroup]);

  const addonIds = filteredAddons.map((addon) => addon.id);

  // Count all of the addons that are a part of this addon group
  const count = Object.entries(addonCounts).reduce((total, [key, value]) => {
    if (addonIds.includes(parseInt(key, 10))) {
      return total + value?.count;
    }
    return total;
  }, 0);

  const validate = () => {
    let valid = true;

    // const hasCorrectCount = count === booking.guests;

    const allAddonsAboveMin = isEmpty(
      Object.entries(addonCounts).find(([id, addonCount]) => {
        if (addonCount === 0) {
          devLog('info', 'Invalid form', 'Incorrect count');
          return false;
        }

        const addon = findObjectByProperty(addonGroup.addons, id, 'id');

        if (addon?.min && addonCount < addon?.min) {
          devLog('info', 'Invalid form', 'addons are not above min');
        }

        return addon?.min && addonCount < addon?.min;
      }),
    );

    const allAddonsBelowMax = isEmpty(
      Object.entries(addonCounts).find(([key, addonCount]) => {
        if (addonCount === 0) {
          devLog('info', 'Invalid form', 'Incorrect count');
          return false;
        }
        const addon = findObjectByProperty(addonGroup.addons, key, 'id');

        if (addon?.max && addonCount > addon?.max) {
          devLog('info', 'Invalid form', 'addons are not below max');
        }

        return addon?.max && addonCount > addon?.max;
      }),
    );

    // if (!hasCorrectCount) {
    //   valid = false;
    //   rollbar.warning('Invalid guest amount for addons');
    //   devLog('info', 'Invalid form', 'Invalid guest amount for addons');
    // }

    if (!allAddonsAboveMin) {
      valid = false;
      rollbar.warning('Addons are not above min');
      devLog('info', 'Invalid form', 'Addons are not above min');
    }

    if (!allAddonsBelowMax) {
      valid = false;
      rollbar.warning('Addons are not below max');
      devLog('info', 'Invalid form', 'Addons are not below max');
    }

    if (!valid) {
      // TODO show error message somewhere
      setCountErrors(ERROR_MESSAGES.requiredField);
    } else {
      setCountErrors(null);

      // Update the held addons so nobody else takes it
      if (!isEmpty(addonCounts)) {
        // For accommodation bookings, update the addons and the room
        if (isAccommodation) {
          const addonCount = addonCounts?.[addonGroup?.id];

          // We need to send the rooms when holding the accommodation experiences
          const selectedRooms = accommodation?.data?.selectableRooms.slice(
            0,
            addonCount?.count || 1,
          );

          // If we've already held rooms, we're updating
          if (booking?.[bookingKeys.hasHeldRooms]) {
            updateHeldExperience(
              obeeBooking?.data?.id ?? booking?.heldBookingId,
              booking?.selectedExperiences,
              addonCounts,
              selectedRooms,
            );
          } else {
            // Otherwise hold the rooms, specific to accommodation
            holdSelectedRooms(
              obeeBooking?.data?.id ?? booking?.heldBookingId,
              addonCounts,
              selectedRooms,
            );
          }
        } else {
          // Update the addons too
          updateHeldExperience(
            obeeBooking?.data?.id ?? booking?.heldBookingId,
            booking?.selectedExperiences,
            addonCounts,
          );
        }
      }

      updateBooking(bookingKeys.addons, addonCounts);
    }

    return valid;
  };

  const submit = () => {
    trackEvent('Navigation', 'Updating Step', 'Next Button');
    const valid = validate();

    if (valid) {
      onNext();
      // updateBookingStep(bookingSteps.review);
    }
  };

  const isExclusiveSelect = addonGroup?.isExclusiveSelect;
  // per_group addon handle function
  const handleClick = (addon) => {
    const newAddonCounts = { ...addonCounts };
    if (isExclusiveSelect) {
      if (addon.addOnMultiplier === 'per_group') {
        // If the addon is already selected, toggle its count to 0
        if (addonCounts[addon.id]?.count >= 1) {
          newAddonCounts[addon.id] = { ...newAddonCounts[addon.id], count: 0 };
        } else {
          // Reset all other addon's counts to 0
          Object.keys(newAddonCounts).forEach((id) => {
            if (newAddonCounts[id]?.addonGroupId === addonGroup?.id) {
              newAddonCounts[id] = { ...newAddonCounts[id], count: 0 };
            }
          });

          // Set the clicked addon to count 1
          newAddonCounts[addon.id] = {
            addon,
            // count: 1,
            count: addon?.forceSingleQuantity ? 1 : booking.guests,
            compulsory: addonGroup.compulsory,
            groupIndex,
            addonGroupId: addonGroup?.id,
          };
        }
        setAddonCounts(newAddonCounts);
      }
    } else {
      if (newAddonCounts[addon.id]?.count > 0) {
        newAddonCounts[addon.id] = {
          addon,
          count: 0,
          compulsory: addonGroup.compulsory,
          groupIndex,
          addonGroupId: addonGroup?.id,
        };
        setAddonCounts(newAddonCounts);
        return;
      }

      newAddonCounts[addon.id] = {
        addon,
        count: addon?.forceSingleQuantity ? 1 : booking.guests,
        compulsory: addonGroup.compulsory,
        groupIndex,
        addonGroupId: addonGroup?.id,
      };
      setAddonCounts(newAddonCounts);
    }
  };

  const updateAddonCount = (addon, newValue) => {
    const newAddonCounts = { ...addonCounts };

    if (isExclusiveSelect) {
      if (addon.addOnMultiplier === 'per_group') {
        handleClick(addon);
      } else {
        // For per_person multiplier, just update the count while ensuring other addons are reset
        Object.keys(newAddonCounts).forEach((id) => {
          if (id !== addon.id.toString() && newAddonCounts[id]?.addonGroupId === addonGroup?.id) {
            newAddonCounts[id] = { ...newAddonCounts[id], count: 0 };
          }
        });

        newAddonCounts[addon.id] = {
          addon,
          count: newValue,
          compulsory: addonGroup.compulsory,
          groupIndex,
          addonGroupId: addonGroup?.id,
        };
        setAddonCounts(newAddonCounts);
      }
    } else {
      newAddonCounts[addon.id] = {
        addon,
        count: newValue,
        compulsory: addonGroup.compulsory,
        groupIndex,
        addonGroupId: addonGroup?.id,
      };
      setAddonCounts(newAddonCounts);
    }
  };

  // Sigh. If it's an accommodation booking, preselect the addon with a count of one
  // TODO clean this up and have something more generic on the addon group
  // TODO this shouldn't be necessary anyway as it should use the minSelections
  const hasSelectableRoms = accommodation?.data?.selectableRooms?.length > 0;
  useEffect(() => {
    if (isAccommodation && isEmpty(addonCounts) && hasSelectableRoms) {
      updateAddonCount(filteredAddons?.[0], 1);
    }
  }, []);

  // const hasMinSelections = addonGroup?.minSelections > 0;

  const heading = addonGroup?.headingLabel ?? 'Make your visit extra special';

  const getMaxGuestsForAddon = (addon) => {
    // return isAccommodation
    //   ? accommodation?.data?.selectableRooms?.length ?? booking.guests ?? 10
    //   : addonGroup?.matchGuests
    //   ? booking.guests
    //   : addon?.availableQuantity;

    if (isAccommodation) {
      return accommodation?.data?.selectableRooms?.length ?? booking.guests ?? 10;
    }

    if (addon?.max > 0) {
      return addon.max;
    }

    if (addonGroup?.matchGuests) {
      return booking.guests;
    }

    return addon?.availableQuantity;
  };

  const enableNextBtn = () => {
    const { compulsory, matchGuests } = addonGroup;

    if (isAccommodation && !hasSelectableRoms) {
      return false;
    }

    if (compulsory) {
      if (matchGuests) {
        return count === booking.guests;
      }
      return count >= 1;
    }

    if (!compulsory) {
      if (matchGuests) {
        return count === 0 || count === booking.guests;
      }
      return true;
    }
    return true;
  };

  const showPrice = (addon) => {
    const hideZeroDollarPrice = safeParse(restaurant?.data?.bookingWidgetSettings)?.widgetSettings
      ?.settingsDetails?.hideZeroDollarPrice;

    if (hideZeroDollarPrice === true && (isEmpty(addon?.price) || addon?.price === 0)) {
      return false;
    }

    return isAccommodation ? addonGroup?.showAccommodationStepPrice : addon?.showPrice;
  };

  return (
    <Spacer direction='vertical' gap={32} className={classes.addonContent}>
      <Spacer direction='vertical' gap={20}>
        <Typography variant='h2'>{heading}</Typography>
      </Spacer>

      <Spacer direction='vertical' gap={36}>
        <Divider color={theme.colors.dividerColor} />
        {filteredAddons.map((addon) => {
          return (
            <Fragment key={addon?.id}>
              {hasImages ? (
                <AddonOption
                  addon={addon}
                  maxGuests={getMaxGuestsForAddon(addon)}
                  onChange={(newValue) => {
                    updateAddonCount(addon, newValue);
                  }}
                  value={addonCounts[addon.id]?.count || 0}
                  isAccommodation={isAccommodation}
                  showPrice={showPrice(addon)}
                  hasSelectableRoms={hasSelectableRoms}
                  handleClick={() => handleClick(addon)}
                  isExclusiveSelect={isExclusiveSelect}
                />
              ) : (
                <AddonOptionNoImage
                  addon={addon}
                  maxGuests={getMaxGuestsForAddon(addon)}
                  onChange={(newValue) => {
                    updateAddonCount(addon, newValue);
                  }}
                  value={addonCounts[addon.id]?.count || 0}
                  isAccommodation={isAccommodation}
                  showPrice={showPrice(addon)}
                  hasSelectableRoms={hasSelectableRoms}
                  handleClick={() => handleClick(addon)}
                  isExclusiveSelect={isExclusiveSelect}
                />
              )}

              <Divider color={theme.colors.dividerColor} style={{ height: '2px' }} />
            </Fragment>
          );
        })}
      </Spacer>

      <Spacer style={{ justifyContent: 'space-between' }}>
        {!addonGroup?.compulsory && !addonGroup?.matchGuests ? (
          <Box />
        ) : addonGroup?.matchGuests ? (
          <Box>
            {count} / {booking.guests} selected
          </Box>
        ) : (
          <Box>{count || 'None'} selected</Box>
        )}

        <Box style={{ display: 'flex' }}>
          <Button
            size='large'
            onClick={submit}
            style={{ marginLeft: 'auto', minWidth: '144px' }}
            fullWidth={isMobile}
            color='primary'
            disabled={!enableNextBtn()}
          >
            Next
          </Button>
        </Box>
      </Spacer>
    </Spacer>
  );
};
AddonGroup.propTypes = {
  booking: bookingPropTypes.isRequired,
  obeeBooking: obeeBookingPropTypes.isRequired,
  updateBooking: PropTypes.func.isRequired,
  addonGroup: addonGroupPropTypes.isRequired,
  onNext: PropTypes.func.isRequired,
  groupIndex: PropTypes.number.isRequired,
  updateHeldExperience: PropTypes.func.isRequired,
  holdSelectedRooms: PropTypes.func.isRequired,
  isAccommodation: PropTypes.bool.isRequired,
  accommodation: accommodationPropTypes.isRequired,
  restaurant: restaurantPropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  obeeBooking: state.obeeBooking,
  bookingTerms: state.bookingTerms,
  accommodation: state.accommodation,
  restaurant: state.restaurant,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchBookingTerms: fetchBookingTermsAction,
      updateBooking: updateBookingAction,
      holdBooking: holdBookingAction,
      updateHeldExperience: updateHeldExperienceAction,
      holdSelectedRooms: holdSelectedRoomsAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(AddonGroup);
